/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import AceEditor from 'react-ace';
import { keysOf } from '../common';
import { htmlIdGenerator, keyCodes } from '../../services';
import { EuiI18n } from '../i18n';
var DEFAULT_MODE = 'text';
var DEFAULT_THEME = 'textmate';

function setOrRemoveAttribute(element, attributeName, value) {
  if (value === null || value === undefined) {
    element.removeAttribute(attributeName);
  } else {
    element.setAttribute(attributeName, value);
  }
}

export class EuiCodeEditor extends Component {
  static defaultProps = {
    setOptions: {}
  };
  state = {
    isHintActive: true,
    isEditing: false,
    name: htmlIdGenerator()()
  };
  idGenerator = htmlIdGenerator();
  aceEditor = null;
  editorHint = null;
  aceEditorRef = aceEditor => {
    if (aceEditor) {
      this.aceEditor = aceEditor;
      const textbox = aceEditor.editor.textInput.getElement();
      textbox.tabIndex = -1;
      textbox.addEventListener('keydown', this.onKeydownAce);
      setOrRemoveAttribute(textbox, 'aria-label', this.props['aria-label']);
      setOrRemoveAttribute(textbox, 'aria-labelledby', this.props['aria-labelledby']);
      setOrRemoveAttribute(textbox, 'aria-describedby', this.props['aria-describedby']);
    }
  };
  onKeydownAce = event => {
    if (event.keyCode === keyCodes.ESCAPE) {
      // If the autocompletion context menu is open then we want to let ESCAPE close it but
      // **not** exit out of editing mode.
      if (this.aceEditor !== null && !this.aceEditor.editor.completer) {
        event.preventDefault();
        event.stopPropagation();
        this.stopEditing();

        if (this.editorHint) {
          this.editorHint.focus();
        }
      }
    }
  };
  onFocusAce = (event, editor) => {
    this.setState({
      isEditing: true
    });

    if (this.props.onFocus) {
      this.props.onFocus(event, editor);
    }
  };
  onBlurAce = (event, editor) => {
    this.stopEditing();

    if (this.props.onBlur) {
      this.props.onBlur(event, editor);
    }
  };
  onKeyDownHint = event => {
    if (event.keyCode === keyCodes.ENTER) {
      event.preventDefault();
      this.startEditing();
    }
  };
  startEditing = () => {
    this.setState({
      isHintActive: false
    });

    if (this.aceEditor !== null) {
      this.aceEditor.editor.textInput.focus();
    }
  };

  stopEditing() {
    this.setState({
      isHintActive: true,
      isEditing: false
    });
  }

  isCustomMode() {
    return typeof this.props.mode === 'object';
  }

  setCustomMode() {
    if (this.aceEditor !== null) {
      this.aceEditor.editor.getSession().setMode(this.props.mode);
    }
  }

  componentDidMount() {
    if (this.isCustomMode()) {
      this.setCustomMode();
    }

    const {
      isReadOnly,
      id
    } = this.props;
    const textareaProps = {
      id,
      readOnly: isReadOnly
    };
    const el = document.getElementById(this.state.name);

    if (el) {
      const textarea = el.querySelector('textarea');
      if (textarea) keysOf(textareaProps).forEach(key => {
        if (textareaProps[key]) textarea.setAttribute(`${key}`, textareaProps[key].toString());
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.mode !== prevProps.mode && this.isCustomMode()) {
      this.setCustomMode();
    }
  }

  render() {
    const {
      width,
      height,
      onBlur,
      // eslint-disable-line no-unused-vars
      isReadOnly,
      setOptions,
      cursorStart,
      mode = DEFAULT_MODE,
      'data-test-subj': dataTestSubj = 'codeEditorContainer',
      theme = DEFAULT_THEME,
      ...rest
    } = this.props;
    const classes = classNames('euiCodeEditorWrapper', {
      'euiCodeEditorWrapper-isEditing': this.state.isEditing
    });
    const promptClasses = classNames('euiCodeEditorKeyboardHint', {
      'euiCodeEditorKeyboardHint-isInactive': !this.state.isHintActive
    });
    let filteredCursorStart;
    const options = { ...setOptions
    };

    if (isReadOnly) {
      // Put the cursor at the beginning of the editor, so that it doesn't look like
      // a prompt to begin typing.
      filteredCursorStart = -1;
      Object.assign(options, {
        readOnly: true,
        highlightActiveLine: false,
        highlightGutterLine: false
      });
    } else {
      filteredCursorStart = cursorStart;
    } // Don't use EuiKeyboardAccessible here because it doesn't play nicely with onKeyDown.


    const prompt = <div className={promptClasses} id={this.idGenerator('codeEditor')} ref={hint => {
      this.editorHint = hint;
    }} tabIndex={0} role="button" onClick={this.startEditing} onKeyDown={this.onKeyDownHint} data-test-subj="codeEditorHint">
        <p className="euiText">
          {isReadOnly ? <EuiI18n token="euiCodeEditor.startInteracting" default="Press Enter to start interacting with the code." /> : <EuiI18n token="euiCodeEditor.startEditing" default="Press Enter to start editing." />}
        </p>

        <p className="euiText">
          {isReadOnly ? <EuiI18n token="euiCodeEditor.stopInteracting" default="When you're done, press Escape to stop interacting with the code." /> : <EuiI18n token="euiCodeEditor.stopEditing" default="When you're done, press Escape to stop editing." />}
        </p>
      </div>;
    return <div className={classes} style={{
      width,
      height
    }} data-test-subj={dataTestSubj}>
        {prompt}

        <AceEditor // Setting a default, existing `mode` is necessary to properly initialize the editor
      // prior to dynamically setting a custom mode (https://github.com/elastic/eui/pull/2616)
      mode={this.isCustomMode() ? DEFAULT_MODE : mode} // https://github.com/securingsincity/react-ace/pull/771
      name={this.state.name} theme={theme} ref={this.aceEditorRef} width={width} height={height} onFocus={this.onFocusAce} onBlur={this.onBlurAce} setOptions={options} editorProps={{
        $blockScrolling: Infinity
      }} cursorStart={filteredCursorStart} {...rest} />
      </div>;
  }

}
EuiCodeEditor.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  onBlur: PropTypes.any,
  onFocus: PropTypes.any,
  isReadOnly: PropTypes.bool,
  setOptions: PropTypes.any.isRequired,
  cursorStart: PropTypes.number,
  "data-test-subj": PropTypes.string,

  /**
     * Select the `brace` theme
     * The matching theme file must also be imported from `brace` (e.g., `import 'brace/theme/github';`)
     */
  theme: PropTypes.any,

  /**
     * Use string for a built-in mode or object for a custom mode
     */
  mode: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]),
  id: PropTypes.string
};

try {
  // @ts-ignore
  EuiCodeEditor.displayName = "EuiCodeEditor"; // @ts-ignore

  EuiCodeEditor.__docgenInfo = {
    "description": "",
    "displayName": "EuiCodeEditor",
    "props": {
      "width": {
        "defaultValue": null,
        "description": "",
        "name": "width",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "height": {
        "defaultValue": null,
        "description": "",
        "name": "height",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onBlur": {
        "defaultValue": null,
        "description": "",
        "name": "onBlur",
        "required": false,
        "type": {
          "name": "((event: any, editor?: AceEditorClass | undefined) => void) | undefined"
        }
      },
      "onFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onFocus",
        "required": false,
        "type": {
          "name": "((event: any, editor?: AceEditorClass | undefined) => void) | undefined"
        }
      },
      "isReadOnly": {
        "defaultValue": null,
        "description": "",
        "name": "isReadOnly",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "setOptions": {
        "defaultValue": {
          value: "{}"
        },
        "description": "",
        "name": "setOptions",
        "required": false,
        "type": {
          "name": "IAceOptions | undefined"
        }
      },
      "cursorStart": {
        "defaultValue": null,
        "description": "",
        "name": "cursorStart",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "theme": {
        "defaultValue": null,
        "description": "Select the `brace` theme\nThe matching theme file must also be imported from `brace` (e.g., `import 'brace/theme/github';`)",
        "name": "theme",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "mode": {
        "defaultValue": null,
        "description": "Use string for a built-in mode or object for a custom mode",
        "name": "mode",
        "required": false,
        "type": {
          "name": "string | object | undefined"
        }
      },
      "id": {
        "defaultValue": null,
        "description": "",
        "name": "id",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-describedby": {
        "defaultValue": null,
        "description": "Identifies the element (or elements) that describes the object.\n@see aria-labelledby",
        "name": "aria-describedby",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "Defines a string value that labels the current element.\n@see aria-labelledby.",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-labelledby": {
        "defaultValue": null,
        "description": "Identifies the element (or elements) that labels the current element.\n@see aria-describedby.",
        "name": "aria-labelledby",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "defaultValue": {
        "defaultValue": null,
        "description": "",
        "name": "defaultValue",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "placeholder": {
        "defaultValue": null,
        "description": "",
        "name": "placeholder",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "style": {
        "defaultValue": null,
        "description": "",
        "name": "style",
        "required": false,
        "type": {
          "name": "CSSProperties | undefined"
        }
      },
      "onCopy": {
        "defaultValue": null,
        "description": "",
        "name": "onCopy",
        "required": false,
        "type": {
          "name": "((value: string) => void) | undefined"
        }
      },
      "onPaste": {
        "defaultValue": null,
        "description": "",
        "name": "onPaste",
        "required": false,
        "type": {
          "name": "((value: string) => void) | undefined"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": false,
        "type": {
          "name": "((value: string, event?: any) => void) | undefined"
        }
      },
      "onInput": {
        "defaultValue": null,
        "description": "",
        "name": "onInput",
        "required": false,
        "type": {
          "name": "((event?: any) => void) | undefined"
        }
      },
      "onLoad": {
        "defaultValue": null,
        "description": "",
        "name": "onLoad",
        "required": false,
        "type": {
          "name": "((editor: IEditorProps) => void) | undefined"
        }
      },
      "onScroll": {
        "defaultValue": null,
        "description": "",
        "name": "onScroll",
        "required": false,
        "type": {
          "name": "((editor: IEditorProps) => void) | undefined"
        }
      },
      "fontSize": {
        "defaultValue": null,
        "description": "",
        "name": "fontSize",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "value": {
        "defaultValue": null,
        "description": "",
        "name": "value",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "name": {
        "defaultValue": null,
        "description": "",
        "name": "name",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "focus": {
        "defaultValue": null,
        "description": "",
        "name": "focus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "readOnly": {
        "defaultValue": null,
        "description": "",
        "name": "readOnly",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showGutter": {
        "defaultValue": null,
        "description": "",
        "name": "showGutter",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showPrintMargin": {
        "defaultValue": null,
        "description": "",
        "name": "showPrintMargin",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "highlightActiveLine": {
        "defaultValue": null,
        "description": "",
        "name": "highlightActiveLine",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "wrapEnabled": {
        "defaultValue": null,
        "description": "",
        "name": "wrapEnabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "minLines": {
        "defaultValue": null,
        "description": "",
        "name": "minLines",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "maxLines": {
        "defaultValue": null,
        "description": "",
        "name": "maxLines",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "navigateToFileEnd": {
        "defaultValue": null,
        "description": "",
        "name": "navigateToFileEnd",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "debounceChangePeriod": {
        "defaultValue": null,
        "description": "",
        "name": "debounceChangePeriod",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "enableBasicAutocompletion": {
        "defaultValue": null,
        "description": "",
        "name": "enableBasicAutocompletion",
        "required": false,
        "type": {
          "name": "boolean | string[] | undefined"
        }
      },
      "enableLiveAutocompletion": {
        "defaultValue": null,
        "description": "",
        "name": "enableLiveAutocompletion",
        "required": false,
        "type": {
          "name": "boolean | string[] | undefined"
        }
      },
      "tabSize": {
        "defaultValue": null,
        "description": "",
        "name": "tabSize",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "scrollMargin": {
        "defaultValue": null,
        "description": "",
        "name": "scrollMargin",
        "required": false,
        "type": {
          "name": "number[] | undefined"
        }
      },
      "enableSnippets": {
        "defaultValue": null,
        "description": "",
        "name": "enableSnippets",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "onSelectionChange": {
        "defaultValue": null,
        "description": "",
        "name": "onSelectionChange",
        "required": false,
        "type": {
          "name": "((value: any, event?: any) => void) | undefined"
        }
      },
      "onCursorChange": {
        "defaultValue": null,
        "description": "",
        "name": "onCursorChange",
        "required": false,
        "type": {
          "name": "((value: any, event?: any) => void) | undefined"
        }
      },
      "onValidate": {
        "defaultValue": null,
        "description": "",
        "name": "onValidate",
        "required": false,
        "type": {
          "name": "((annotations: Annotation[]) => void) | undefined"
        }
      },
      "onBeforeLoad": {
        "defaultValue": null,
        "description": "",
        "name": "onBeforeLoad",
        "required": false,
        "type": {
          "name": "((ace: any) => void) | undefined"
        }
      },
      "onSelection": {
        "defaultValue": null,
        "description": "",
        "name": "onSelection",
        "required": false,
        "type": {
          "name": "((selectedText: string, event?: any) => void) | undefined"
        }
      },
      "editorProps": {
        "defaultValue": null,
        "description": "",
        "name": "editorProps",
        "required": false,
        "type": {
          "name": "IEditorProps | undefined"
        }
      },
      "keyboardHandler": {
        "defaultValue": null,
        "description": "",
        "name": "keyboardHandler",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "commands": {
        "defaultValue": null,
        "description": "",
        "name": "commands",
        "required": false,
        "type": {
          "name": "ICommand[] | undefined"
        }
      },
      "annotations": {
        "defaultValue": null,
        "description": "",
        "name": "annotations",
        "required": false,
        "type": {
          "name": "Annotation[] | undefined"
        }
      },
      "markers": {
        "defaultValue": null,
        "description": "",
        "name": "markers",
        "required": false,
        "type": {
          "name": "IMarker[] | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/code_editor/code_editor.tsx#EuiCodeEditor"] = {
      docgenInfo: EuiCodeEditor.__docgenInfo,
      name: "EuiCodeEditor",
      path: "src/components/code_editor/code_editor.tsx#EuiCodeEditor"
    };
} catch (__react_docgen_typescript_loader_error) {}